import React, { useState } from 'react';
import api from '../../services/api';

const money = (value: number) =>
  Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  });
const control = {
  padding: 9,
  borderRadius: 6,
  margin: '6px 6px 6px 0',
  maxWidth: '100%'
};
export default function AccountingSync({
  connection,
  onRefresh
}: {
  connection: any;
  onRefresh: () => Promise<void>;
}) {
  const [catalog, setCatalog] = useState<any>(null),
    [jobs, setJobs] = useState<any[]>([]),
    [jobId, setJobId] = useState('');
  const [preview, setPreview] = useState<any>(null),
    [remote, setRemote] = useState<any>({}),
    [customer, setCustomer] = useState('');
  const [item, setItem] = useState(''),
    [deposit, setDeposit] = useState(''),
    [choices, setChoices] = useState<Record<string, string>>({});
  const [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState('');
  const state = connection.accounting;
  const inactiveCustomer =
    catalog?.customers.find((c: any) => c.id === customer)?.active === false;
  const act = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
    } catch (e: any) {
      setError(
        e.response?.data?.error ||
          'Could not complete this review. Refresh and try again.'
      );
    } finally {
      setBusy(false);
    }
  };
  const load = async () => {
    const [assets, jobResponse] = await Promise.all([
      api.get('/quickbooks/accounting/catalogs'),
      api.get('/jobs')
    ]);
    setCatalog(assets.data);
    setJobs(jobResponse.data);
    setItem(state?.config?.itemId || '');
    setDeposit(state?.config?.depositAccountId || '');
  };
  const review = async (id: string) => {
    setJobId(id);
    setPreview(null);
    setChoices({});
    setRemote({});
    if (!id) return;
    const p = (await api.get(`/quickbooks/accounting/jobs/${id}`)).data;
    setPreview(p);
    setCustomer(p.mapping?.customer_id || '');
    if (p.mapping && state?.enabled)
      setRemote(
        (await api.get(`/quickbooks/accounting/jobs/${id}/remote-records`)).data
      );
  };
  if (!connection.verified) return null;
  return (
    <section
      style={{
        marginTop: 24,
        padding: 24,
        border: '1px solid #2D4259',
        borderRadius: 14,
        background: '#1A2B3C'
      }}
    >
      <h2>Invoice and receipt sync</h2>
      <p>
        Company: <strong>{connection.companyName}</strong>
      </p>
      {!state?.schemaReady ? (
        <p role="alert">
          The accounting sync database update is required before setup. Your CRM
          invoices and receipts remain saved.
        </p>
      ) : (
        <>
          {state.records?.some((r: any) => r.status === 'needs_review') && (
            <p role="alert">
              <strong>Some records need accounting review.</strong> Open Review
              sync setup to match their customer and property or resolve an
              existing transaction.
            </p>
          )}
          <p>
            {state.enabled
              ? 'Sync is enabled for reviewed job mappings.'
              : 'Sync is paused until accounting accounts and job mappings are reviewed.'}{' '}
            Existing transactions require individual review before they are
            added or linked.
          </p>
          {state.companyMatches === false && (
            <p role="alert">
              The connected company differs from the saved mappings. Posting is
              blocked.
            </p>
          )}
          {!state.webhookConfigured && (
            <p role="alert">
              QuickBooks inbound notifications are not configured. Enter the
              Intuit verifier token in the server settings before accepting
              notifications. Record received payments from verified accounting
              records.
            </p>
          )}
          {!!state.inboundEvents?.length && (
            <details>
              <summary>
                {state.inboundEvents.length} verified QuickBooks notifications
                require accounting review
              </summary>
              <p>
                Notifications do not mark CRM invoices paid. Check the current
                QuickBooks transaction, then record or reconcile the actual
                receipt once in CRM.
              </p>
              {state.inboundEvents.map((e: any) => (
                <div key={e.id}>
                  {new Date(e.received_at).toLocaleString()}:{' '}
                  {e.entity_refs
                    .map((r: any) => `${r.type} ${r.id}`)
                    .join(', ')}
                </div>
              ))}
            </details>
          )}
          {!!state.billingLinks?.length && (
            <details>
              <summary>Customer billing requests needing attention</summary>
              {state.billingLinks.map((r: any) => (
                <p key={r.job_id}>
                  {r.status}:{' '}
                  {r.last_error ||
                    'Awaiting the original billing request to finish.'}
                </p>
              ))}
            </details>
          )}
          <p>
            Invoices are mirrored without sending customer emails or enabling
            payment links. Receipts go to Undeposited Funds for matching to real
            bank deposits.
          </p>
          <button style={control} disabled={busy} onClick={() => act(load)}>
            Review sync setup
          </button>
          <button
            style={control}
            disabled={busy}
            onClick={() => act(onRefresh)}
          >
            Refresh status
          </button>
          {state.enabled && (
            <button
              style={control}
              disabled={busy}
              onClick={() =>
                act(async () => {
                  await api.put('/quickbooks/accounting/config', {
                    realmId: connection.realmId,
                    itemId: state.config.itemId,
                    depositAccountId: state.config.depositAccountId,
                    enabled: false
                  });
                  await onRefresh();
                  setNotice(
                    'Automatic posting paused. New CRM changes remain captured for review.'
                  );
                })
              }
            >
              Pause sync
            </button>
          )}
          {catalog && (
            <div>
              <h3>Accounting accounts</h3>
              <label>
                Service item and income account
                <br />
                <select
                  aria-label="Service item"
                  style={control}
                  value={item}
                  onChange={(e) => setItem(e.target.value)}
                >
                  <option value="">Choose an item</option>
                  {catalog.items.map((x: any) => (
                    <option key={x.id} value={x.id}>
                      {x.name} (income account {x.incomeAccountId})
                    </option>
                  ))}
                </select>
              </label>
              <br />
              <label>
                Receipt holding account
                <br />
                <select
                  aria-label="Receipt holding account"
                  style={control}
                  value={deposit}
                  onChange={(e) => setDeposit(e.target.value)}
                >
                  <option value="">Choose Undeposited Funds</option>
                  {catalog.depositAccounts.map((x: any) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </label>
              <br />
              <button
                style={control}
                disabled={busy || !item || !deposit}
                onClick={() =>
                  act(async () => {
                    await api.put('/quickbooks/accounting/config', {
                      realmId: catalog.realmId,
                      itemId: item,
                      depositAccountId: deposit,
                      enabled: true
                    });
                    await onRefresh();
                    setNotice(
                      'Accounting accounts saved. Review each job customer before new activity can sync.'
                    );
                  })
                }
              >
                Save accounts and enable reviewed jobs
              </button>
              <h3>Customer and property review</h3>
              <select
                aria-label="CRM job"
                style={control}
                value={jobId}
                disabled={busy}
                onChange={(e) => act(() => review(e.target.value))}
              >
                <option value="">Choose a CRM job</option>
                {jobs.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.homeowner_name} — {j.address} ({j.job_number})
                  </option>
                ))}
              </select>
              <button
                style={control}
                disabled={busy || !jobId}
                onClick={() => act(() => review(jobId))}
              >
                Refresh job review
              </button>
              {preview && (
                <div>
                  <p>
                    <strong>{preview.job.name}</strong>
                    <br />
                    {preview.job.address}
                    <br />
                    {preview.job.number}
                  </p>
                  <label>
                    QuickBooks customer
                    <br />
                    <select
                      aria-label="QuickBooks customer"
                      style={control}
                      value={customer}
                      disabled={busy || !!preview.mapping}
                      onChange={(e) => setCustomer(e.target.value)}
                    >
                      <option value="">
                        Choose the matching customer and property
                      </option>
                      <option value="new">
                        Create a separate customer for this CRM job
                      </option>
                      {catalog.customers.map((c: any) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                          {c.active === false ? ' (inactive)' : ''} —{' '}
                          {[c.address?.Line1, c.address?.City]
                            .filter(Boolean)
                            .join(', ')}
                        </option>
                      ))}
                    </select>
                  </label>
                  {inactiveCustomer && (
                    <p role="alert">
                      This customer is inactive in QuickBooks. Review its
                      existing transactions and reactivate it there before
                      mapping or syncing. Do not create a replacement to bypass
                      this review.
                    </p>
                  )}
                  {!preview.mapping && (
                    <>
                      <p>
                        Review both the customer and property. Job numbers from
                        older systems can refer to different jobs. Saving this
                        mapping enables future records; existing invoices and
                        receipts stay on hold.
                      </p>
                      <button
                        style={control}
                        disabled={busy || !customer || inactiveCustomer}
                        onClick={() =>
                          act(async () => {
                            await api.put(
                              `/quickbooks/accounting/jobs/${jobId}/mapping`,
                              {
                                realmId: catalog.realmId,
                                customerId:
                                  customer === 'new' ? undefined : customer,
                                createCustomer: customer === 'new',
                                customerVersion: catalog.customers.find(
                                  (c: any) => c.id === customer
                                )?.version,
                                reviewVersion: preview.reviewVersion
                              }
                            );
                            await review(jobId);
                            setNotice(
                              'Customer mapping saved. Review any existing transactions individually below.'
                            );
                          })
                        }
                      >
                        Save reviewed customer mapping
                      </button>
                    </>
                  )}
                  {preview.mapping && (
                    <>
                      <button
                        style={control}
                        disabled={busy || inactiveCustomer}
                        onClick={() =>
                          act(async () => {
                            await api.put(
                              `/quickbooks/accounting/jobs/${jobId}/mapping`,
                              {
                                realmId: catalog.realmId,
                                customerId: customer,
                                customerVersion: catalog.customers.find(
                                  (c: any) => c.id === customer
                                )?.version,
                                reviewVersion: preview.reviewVersion
                              }
                            );
                            await review(jobId);
                            setNotice('Current customer identity reviewed.');
                          })
                        }
                      >
                        Reconfirm customer and property
                      </button>
                      <h3>Existing invoices and receipts</h3>
                      <p>
                        Choose an existing QuickBooks entry when it represents
                        this same transaction. Create a new entry only after
                        checking for duplicates. Linking permits future CRM
                        edits to update its accounting fields; changes made
                        directly in QuickBooks will pause sync for review.
                      </p>
                      {preview.entities.map((x: any) => (
                        <div
                          key={x.kind + x.id}
                          style={{
                            padding: '12px 0',
                            borderTop: '1px solid #2D4259'
                          }}
                        >
                          <strong>
                            {x.number} · {x.kind} · {money(x.amount)}
                          </strong>
                          <div>
                            {x.date} · {x.status} · Sync:{' '}
                            {x.syncStatus.replaceAll('_', ' ')}
                          </div>
                          {x.lastError && <p role="alert">{x.lastError}</p>}
                          <select
                            aria-label={`QuickBooks entry for ${x.number}`}
                            style={control}
                            value={choices[x.id] || ''}
                            onChange={(e) =>
                              setChoices({ ...choices, [x.id]: e.target.value })
                            }
                          >
                            <option value="">
                              Choose a reviewed accounting entry
                            </option>
                            <option value="new">
                              Create new — no duplicate found
                            </option>
                            {(remote[x.kind] || []).map((r: any) => (
                              <option key={r.id} value={r.id}>
                                {r.number || r.id} · {r.date} ·{' '}
                                {money(r.amount)}
                                {r.balance !== undefined
                                  ? ` · balance ${money(r.balance)}`
                                  : ''}
                              </option>
                            ))}
                          </select>
                          <button
                            style={control}
                            disabled={
                              busy ||
                              !state.enabled ||
                              inactiveCustomer ||
                              !choices[x.id]
                            }
                            onClick={() =>
                              act(async () => {
                                const choice = choices[x.id];
                                await api.post(
                                  `/quickbooks/accounting/jobs/${jobId}/approve`,
                                  {
                                    reviewVersion: preview.reviewVersion,
                                    kind: x.kind,
                                    id: x.id,
                                    remoteId:
                                      choice === 'new' ? undefined : choice,
                                    remoteVersion: (remote[x.kind] || []).find(
                                      (r: any) => r.id === choice
                                    )?.version,
                                    createNew: choice === 'new'
                                  }
                                );
                                await review(jobId);
                                await onRefresh();
                                setNotice(
                                  'Reviewed entry queued. Check its status after processing.'
                                );
                              })
                            }
                          >
                            Queue reviewed entry
                          </button>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {!!state.records?.length && (
            <details style={{ marginTop: 20 }}>
              <summary>Recent sync activity ({state.records.length})</summary>
              {state.records.map((r: any) => (
                <div
                  key={r.entity_type + r.local_id}
                  style={{
                    padding: '12px 0',
                    borderBottom: '1px solid #2D4259'
                  }}
                >
                  <strong>
                    {r.entity_type}: {r.status.replaceAll('_', ' ')}
                  </strong>
                  <div>
                    {r.job?.homeowner_name} · {r.job?.address} ·{' '}
                    {r.job?.job_number}
                  </div>
                  <div>
                    {r.last_synced_at
                      ? `Last synced ${new Date(r.last_synced_at).toLocaleString()}`
                      : 'Awaiting first sync'}
                  </div>
                  {r.last_error && <p>{r.last_error}</p>}
                  {['error', 'needs_review'].includes(r.status) && (
                    <button
                      style={control}
                      disabled={busy}
                      onClick={() =>
                        act(async () => {
                          await api.post(
                            `/quickbooks/accounting/records/${r.entity_type}/${r.local_id}/retry`
                          );
                          await onRefresh();
                          setNotice('Original operation queued for retry.');
                        })
                      }
                    >
                      Retry original operation
                    </button>
                  )}
                </div>
              ))}
            </details>
          )}
        </>
      )}
      {notice && <p role="status">{notice}</p>}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
